import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-date-picker-reusable',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './date-picker-reusable.html',
})
export class DatePickerReusableComponent implements OnInit {
  private static readonly overlayGap = 8;
  private static readonly overlayMinHeight = 320;

  @Input() control?: FormControl | AbstractControl | null;
  @Input() startControl?: FormControl | AbstractControl | null;
  @Input() endControl?: FormControl | AbstractControl | null;
  @Input() mode: 'single' | 'range' = 'single';
  @Input() size: 'md' | 'lg' = 'lg';
  @Input() placeholder = 'dd-mm-yyyy';
  @Input() maxDate?: string;

  @Output() valueChange = new EventEmitter<any>();

  protected isOpen = false;
  protected openAbove = false;
  protected overlayStyle: Record<string, string> = {};
  protected leftMonth = this.startOfMonth(this.getDefaultCalendarDate());
  protected rightMonth = this.addMonths(this.leftMonth, 1);
  protected weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  protected monthOptions = [
    { label: 'January', value: 0 },
    { label: 'February', value: 1 },
    { label: 'March', value: 2 },
    { label: 'April', value: 3 },
    { label: 'May', value: 4 },
    { label: 'June', value: 5 },
    { label: 'July', value: 6 },
    { label: 'August', value: 7 },
    { label: 'September', value: 8 },
    { label: 'October', value: 9 },
    { label: 'November', value: 10 },
    { label: 'December', value: 11 },
  ];
  protected yearOptions: number[] = [];

  @ViewChild('triggerButton') private triggerButton?: ElementRef<HTMLButtonElement>;

  private readonly hostElement = inject(ElementRef<HTMLElement>);

  private buildYearOptions(): number[] {
    const maxYear = this.maxDate
      ? (this.parseIsoDate(this.maxDate)?.getFullYear() ?? new Date().getFullYear())
      : new Date().getFullYear();
    const years: number[] = [];
    for (let year = maxYear; year >= maxYear - 100; year--) {
      years.push(year);
    }
    return years;
  }

  private getOpeningMonth(): Date {
    if (this.maxDate) {
      const maxParsed = this.parseIsoDate(this.maxDate);
      if (maxParsed) return maxParsed;
    }
    return this.getDefaultCalendarDate();
  }

  ngOnInit(): void {
    this.yearOptions = this.buildYearOptions();
    if (this.mode === 'single' && this.control) {
      const parsed = this.parseIsoDate(this.control.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
    }

    if (this.mode === 'range' && this.startControl) {
      const parsed = this.parseIsoDate(this.startControl.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      this.rightMonth = this.addMonths(this.leftMonth, 1);
    }
  }

  @HostListener('document:click', ['$event'])
  protected onDocClick(event: MouseEvent): void {
    if (!(event.target as HTMLElement).closest('[data-date-picker]')) {
      this.isOpen = false;
    }
  }

  protected toggle(): void {
    if (this.control?.disabled || this.startControl?.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      if (this.mode === 'single') {
        const parsed = this.parseIsoDate(this.control?.value);
        this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      } else {
        const parsed = this.parseIsoDate(this.startControl?.value);
        this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
        this.rightMonth = this.addMonths(this.leftMonth, 1);
      }

      this.updateOverlayPosition();
    }
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    if (this.isOpen) {
      this.updateOverlayPosition();
    }
  }

  @HostListener('window:scroll')
  protected onWindowScroll(): void {
    if (this.isOpen) {
      this.updateOverlayPosition();
    }
  }

  @HostListener('document:scroll')
  protected onDocumentScroll(): void {
    if (this.isOpen) {
      this.updateOverlayPosition();
    }
  }

  private getDefaultCalendarDate(): Date {
    const currentYear = new Date().getFullYear();
    return new Date(currentYear, 0, 1);
  }

  protected clearRange(event?: Event): void {
    event?.stopPropagation();
    if (this.mode === 'range' && this.startControl && this.endControl) {
      this.startControl.setValue('');
      this.endControl.setValue('');
      this.valueChange.emit({ start: '', end: '' });
    }
  }

  protected isDateDisabled(iso: string): boolean {
    if (!this.maxDate) return false;
    return iso > this.maxDate;
  }

  protected isNextMonthDisabled(month: Date): boolean {
    if (!this.maxDate) return false;
    const next = this.addMonths(month, 1);
    const nextIso = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-01`;
    return nextIso > this.maxDate;
  }

  protected selectDate(iso: string): void {
    if (this.isDateDisabled(iso)) return;
    if (this.mode === 'single' && this.control) {
      this.control.setValue(iso);
      this.control.markAsTouched();
      this.valueChange.emit(iso);
      this.isOpen = false;
      return;
    }

    if (this.mode === 'range' && this.startControl && this.endControl) {
      const start = String(this.startControl.value || '');
      const end = String(this.endControl.value || '');

      if (!start || end) {
        this.startControl.setValue(iso);
        this.endControl.setValue('');
        this.valueChange.emit({ start: iso, end: '' });
        return;
      }

      if (iso < start) {
        this.startControl.setValue(iso);
        this.endControl.setValue('');
        this.valueChange.emit({ start: iso, end: '' });
        return;
      }

      this.endControl.setValue(iso);
      this.valueChange.emit({ start, end: iso });
      this.isOpen = false;
    }
  }

  protected isSelected(iso: string): boolean {
    if (this.mode === 'single') {
      return this.normalizeToIsoDate(this.control?.value) === iso;
    }

    const start = this.normalizeToIsoDate(this.startControl?.value) || '';
    const end = this.normalizeToIsoDate(this.endControl?.value) || '';
    return iso === start || iso === end;
  }

  protected isInRange(iso: string): boolean {
    if (this.mode === 'single') return false;
    const start = this.normalizeToIsoDate(this.startControl?.value) || '';
    const end = this.normalizeToIsoDate(this.endControl?.value) || '';
    if (!start || !end) return false;
    return iso > start && iso < end;
  }

  protected getCalendarDays(monthDate: Date): Array<{ iso: string; day: number; inCurrentMonth: boolean }> {
    const start = this.startOfMonth(monthDate);
    const firstWeekday = start.getDay();
    const gridStart = new Date(start);
    gridStart.setDate(start.getDate() - firstWeekday);

    const days: Array<{ iso: string; day: number; inCurrentMonth: boolean }> = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + i);
      days.push({ iso: this.toIsoDate(date), day: date.getDate(), inCurrentMonth: date.getMonth() === monthDate.getMonth() && date.getFullYear() === monthDate.getFullYear() });
    }

    return days;
  }

  protected formatDisplay(value: string): string {
    const parsed = this.parseIsoDate(value);
    if (!parsed) return this.placeholder;
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  }

  protected prevMonth(which: 'left' | 'right'): void {
    if (which === 'left') this.leftMonth = this.addMonths(this.leftMonth, -1);
    else this.rightMonth = this.addMonths(this.rightMonth, -1);
  }

  protected nextMonth(which: 'left' | 'right'): void {
    if (which === 'left') this.leftMonth = this.addMonths(this.leftMonth, 1);
    else this.rightMonth = this.addMonths(this.rightMonth, 1);
  }

  protected onMonthChange(which: 'left' | 'right', value: string): void {
    const month = Number(value);
    if (Number.isNaN(month) || month < 0 || month > 11) return;
    if (which === 'left') this.leftMonth = new Date(this.leftMonth.getFullYear(), month, 1);
    else this.rightMonth = new Date(this.rightMonth.getFullYear(), month, 1);
  }

  protected onYearChange(which: 'left' | 'right', value: string): void {
    const year = Number(value);
    if (Number.isNaN(year)) return;
    if (which === 'left') this.leftMonth = new Date(year, this.leftMonth.getMonth(), 1);
    else this.rightMonth = new Date(year, this.rightMonth.getMonth(), 1);
  }

  private updateOverlayPosition(): void {
    if (!this.triggerButton) {
      return;
    }

    const rect = this.triggerButton.nativeElement.getBoundingClientRect();
    const boundary = this.getOverlayBoundaryRect();
    const gap = DatePickerReusableComponent.overlayGap;
    const spaceBelow = Math.max(boundary.bottom - rect.bottom - gap, 0);
    const spaceAbove = Math.max(rect.top - boundary.top - gap, 0);
    const calendarWidth = Math.min(320, window.innerWidth - 32);
    const left = Math.min(Math.max(rect.left, 16), Math.max(window.innerWidth - calendarWidth - 16, 16));

    this.openAbove =
      spaceBelow < DatePickerReusableComponent.overlayMinHeight && spaceAbove > spaceBelow;

    const maxHeight = Math.max(
      this.openAbove ? spaceAbove : spaceBelow,
      DatePickerReusableComponent.overlayMinHeight,
    );

    this.overlayStyle = {
      left: `${Math.floor(left)}px`,
      width: `${Math.floor(calendarWidth)}px`,
      maxHeight: `${Math.floor(maxHeight)}px`,
      top: this.openAbove ? 'auto' : `${Math.floor(rect.bottom + gap)}px`,
      bottom: this.openAbove ? `${Math.floor(window.innerHeight - rect.top + gap)}px` : 'auto',
    };
  }

  private getOverlayBoundaryRect(): { top: number; bottom: number } {
    let top = 0;
    let bottom = window.innerHeight;
    let currentElement: HTMLElement | null = this.hostElement.nativeElement.parentElement;

    while (currentElement && currentElement !== document.body) {
      const styles = window.getComputedStyle(currentElement);
      const overflowValues = [styles.overflow, styles.overflowY, styles.overflowX];
      const clipsContent = overflowValues.some((value) =>
        ['auto', 'scroll', 'hidden', 'clip'].includes(value),
      );

      if (clipsContent) {
        const rect = currentElement.getBoundingClientRect();
        top = Math.max(top, rect.top);
        bottom = Math.min(bottom, rect.bottom);
      }

      currentElement = currentElement.parentElement;
    }

    if (bottom <= top) {
      return { top: 0, bottom: window.innerHeight };
    }

    return { top, bottom };
  }

  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private addMonths(date: Date, delta: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + delta, 1);
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseIsoDate(value: unknown): Date | null {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const raw = String(value ?? '').trim();
    if (!raw) return null;

    const isoMatch = raw.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:T.*)?$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return this.buildValidatedDate(Number(year), Number(month), Number(day));
    }

    const dmyMatch = raw.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (dmyMatch) {
      const [, day, month, year] = dmyMatch;
      return this.buildValidatedDate(Number(year), Number(month), Number(day));
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }

  private buildValidatedDate(year: number, month: number, day: number): Date | null {
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return null;
    }

    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }

    return parsed;
  }

  private normalizeToIsoDate(value: unknown): string | null {
    const parsed = this.parseIsoDate(value);
    if (!parsed) {
      return null;
    }

    return this.toIsoDate(parsed);
  }
}
